import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  useMediaQuery,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  styled,
  Grid,
  Card,
  CardContent,
  Skeleton,
  Chip,
  Divider,
  Avatar,
  Badge,
  Stack,
  Tooltip,
  IconButton,
  Snackbar,
  Button
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getBirebir, approveLesson, withdrawApproval } from '../../../services/api/birebirService';
import { useAuth } from '../../../contexts/AuthContext';
import { BirebirError } from '../../../types/ErrorModals/BirebirError';
import { format } from 'date-fns/format';
import { tr } from 'date-fns/locale';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SchoolIcon from '@mui/icons-material/School';
import SubjectIcon from '@mui/icons-material/Subject';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import EventIcon from '@mui/icons-material/Event';
import FilterListIcon from '@mui/icons-material/FilterList';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import { getCurrentSezon, getSezonOptions } from '../../../utils/sezon';

// Styled components
const SeasonSelector = styled(FormControl)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius * 1.5,
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  padding: theme.spacing(1.5),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1),
  },
}));

const HeaderTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontWeight: 'bold',
  padding: theme.spacing(1.5),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1),
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.primary.light + '20',
    transition: 'background-color 0.3s ease',
  },
  transition: 'background-color 0.2s',
}));

const EtutCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: theme.shadows[3],
  transition: 'transform 0.3s, box-shadow 0.3s',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[8],
  },
}));

const StatusChip = styled(Chip)(({ theme, status }: { theme: any, status: number }) => ({
  fontWeight: 'bold',
  color: 'white',
  backgroundColor: status === 2 ? theme.palette.error.main : theme.palette.success.main,
  '& .MuiChip-icon': {
    color: 'white',
  },
}));

const Alert2 = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref,
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Birebir: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const { kullaniciModal } = useAuth();
  const [selectedSeason, setSelectedSeason] = useState<string>(String(getCurrentSezon()));
  const availableSeasons = getSezonOptions().map(String);

  // Add filter state
  const [statusFilter, setStatusFilter] = useState<number | null>(null);

  // Add snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const queryClient = useQueryClient();

  const handleSeasonChange = (event: SelectChangeEvent) => {
    setSelectedSeason(event.target.value);
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['birebir', kullaniciModal?.id, selectedSeason],
    queryFn: async () => {
      try {
        return await getBirebir({
          kurumKodu: localStorage.getItem('kurumKodu') || '',
          ogrenciId: kullaniciModal?.id || '',
          sezon: parseInt(selectedSeason)
        });
      } catch (err) {
        const birebirError = err as BirebirError;
        throw new Error(birebirError.message);
      }
    },
    enabled: !!kullaniciModal?.id,
    retry: 1
  });

  // Add mutation hooks
  const approveMutation = useMutation({
    mutationFn: approveLesson,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['birebir', kullaniciModal?.id, selectedSeason]
      });
      setSnackbarMessage('Ders başarıyla onaylandı');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    },
    onError: (error: any) => {
      setSnackbarMessage(error.message || 'Ders onaylanırken bir hata oluştu');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  });

  const withdrawMutation = useMutation({
    mutationFn: withdrawApproval,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['birebir', kullaniciModal?.id, selectedSeason]
      });
      setSnackbarMessage('Ders onayı başarıyla geri çekildi');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    },
    onError: (error: any) => {
      setSnackbarMessage(error.message || 'Ders onayı geri çekilirken bir hata oluştu');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  });

  // Add handler functions
  const handleApproveLesson = (recNo: number) => {
    approveMutation.mutate({
      kurumKodu: localStorage.getItem('kurumKodu') || '',
      recNo,
      ogrenciId: parseInt(kullaniciModal?.id || '0'),
      sezon: parseInt(selectedSeason)
    });
  };

  const handleWithdrawApproval = (recNo: number) => {
    withdrawMutation.mutate({
      kurumKodu: localStorage.getItem('kurumKodu') || '',
      recNo,
      ogrenciId: parseInt(kullaniciModal?.id || '0'),
      sezon: parseInt(selectedSeason)
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (kullaniciModal?.id) {
      refetch();
    }
  }, [selectedSeason, refetch, kullaniciModal?.id]);

  // Filter data based on status
  const filteredData = statusFilter !== null
    ? data?.filter(etut => etut.sonuc === statusFilter)
    : data;

  // Render skeletons for better UX during loading
  const renderSkeletons = () => {
    if (isTablet) {
      return (
        <Grid container spacing={2}>
          {[1, 2, 3].map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item}>
              <Card sx={{ height: '100%', borderRadius: theme.shape.borderRadius * 1.5 }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.primary.main }}>
                  <Skeleton variant="text" width="60%" height={24} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                </Box>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                    <Box>
                      <Skeleton variant="text" width={120} />
                      <Skeleton variant="text" width={80} />
                    </Box>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ mt: 2 }}>
                    <Skeleton variant="text" width="100%" />
                    <Skeleton variant="text" width="70%" />
                  </Box>
                </CardContent>
                <Box sx={{ p: 1.5, bgcolor: theme.palette.grey[100] }}>
                  <Skeleton variant="rectangular" height={32} width={100} />
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      );
    }
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {['Tarih', 'Saat', 'Öğretmen', 'Ders', 'Konu', 'Durum', 'Onay'].map((header) => (
                <TableCell key={header} sx={{ backgroundColor: theme.palette.primary.main }}>
                  <Skeleton variant="text" sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {[1, 2, 3, 4].map((row) => (
              <TableRow key={row}>
                {[1, 2, 3, 4, 5, 6, 7].map((cell) => (
                  <TableCell key={cell}>
                    <Skeleton variant="text" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Render card view for mobile and tablet
  const renderCardView = () => {
    return (
      <Grid container spacing={2}>
        {filteredData?.map((etut, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <EtutCard>
              <Box sx={{
                p: 2,
                bgcolor: theme.palette.primary.main,
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EventIcon />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Birebir Etüt
                  </Typography>
                </Box>
                <StatusChip
                  status={etut.sonuc}
                  label={etut.sonuc === 2 ? 'Derse Gelmedi' : 'Ders Yapıldı'}
                  size="small"
                  icon={etut.sonuc === 2 ? <CancelIcon /> : <CheckCircleIcon />}
                  theme={undefined}
                />
              </Box>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.secondary.main,
                      mr: 2
                    }}
                  >
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {etut.ogretmen.trim()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {etut.ders.trim()}
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarTodayIcon color="action" fontSize="small" />
                    <Typography variant="body2">
                      {format(new Date(etut.dersTarihi), 'dd MMMM yyyy', { locale: tr })}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTimeIcon color="action" fontSize="small" />
                    <Typography variant="body2">
                      {etut.saat2}
                    </Typography>
                  </Box>
                  {etut.konu && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <SubjectIcon color="action" fontSize="small" sx={{ mt: 0.5 }} />
                      <Typography variant="body2">
                        {etut.konu}
                      </Typography>
                    </Box>
                  )}
                </Stack>

                {/* Approval status and buttons */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Onay Durumu:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Chip
                      size="small"
                      label="Öğrenci"
                      color={etut.isStudentApproved ? "success" : "default"}
                      variant={etut.isStudentApproved ? "filled" : "outlined"}
                    />
                    <Chip
                      size="small"
                      label="Öğretmen"
                      color={etut.isTeacherApproved ? "success" : "default"}
                      variant={etut.isTeacherApproved ? "filled" : "outlined"}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    {etut.isStudentApproved ? (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<ThumbDownAltIcon />}
                        onClick={() => handleWithdrawApproval(etut.recNo)}
                        disabled={approveMutation.isPending || withdrawMutation.isPending}
                      >
                        Onayı Geri Çek
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<ThumbUpAltIcon />}
                        onClick={() => handleApproveLesson(etut.recNo)}
                        disabled={approveMutation.isPending || withdrawMutation.isPending}
                      >
                        Onayla
                      </Button>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </EtutCard>
          </Grid>
        ))}
      </Grid>
    );
  };

  // Render table view for desktop
  const renderTableView = () => {
    return (
      <TableContainer
        component={Paper}
        elevation={3}
        sx={{
          minWidth: 800,
          borderRadius: theme.shape.borderRadius * 1.5,
          overflow: 'hidden'
        }}
      >
        <Table aria-label="birebir etüt tablosu">
          <TableHead>
            <TableRow>
              <HeaderTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CalendarTodayIcon sx={{ mr: 1 }} />
                  Tarih
                </Box>
              </HeaderTableCell>
              <HeaderTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AccessTimeIcon sx={{ mr: 1 }} />
                  Saat
                </Box>
              </HeaderTableCell>
              <HeaderTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PersonIcon sx={{ mr: 1 }} />
                  Öğretmen
                </Box>
              </HeaderTableCell>
              <HeaderTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <SchoolIcon sx={{ mr: 1 }} />
                  Ders
                </Box>
              </HeaderTableCell>
              <HeaderTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <SubjectIcon sx={{ mr: 1 }} />
                  Konu
                </Box>
              </HeaderTableCell>
              <HeaderTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CheckCircleIcon sx={{ mr: 1 }} />
                  Durum
                </Box>
              </HeaderTableCell>
              <HeaderTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ThumbUpAltIcon sx={{ mr: 1 }} />
                  Onay
                </Box>
              </HeaderTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData?.map((etut, index) => (
              <StyledTableRow key={index}>
                <StyledTableCell>
                  <Chip
                    label={format(new Date(etut.dersTarihi), 'dd MMMM yyyy', { locale: tr })}
                    size="small"
                    color="primary"
                    variant="outlined"
                    icon={<CalendarTodayIcon />}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <Chip
                    label={etut.saat2}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    icon={<AccessTimeIcon />}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: theme.palette.secondary.main,
                        fontSize: '0.75rem'
                      }}
                    >
                      {etut.ogretmen.trim().charAt(0)}
                    </Avatar>
                    <Typography variant="body2">
                      {etut.ogretmen.trim()}
                    </Typography>
                  </Box>
                </StyledTableCell>
                <StyledTableCell>
                  <Chip
                    label={etut.ders.trim()}
                    size="small"
                    color="default"
                    variant="outlined"
                    icon={<SchoolIcon fontSize="small" />}
                    sx={{
                      bgcolor: theme.palette.background.paper,
                      '& .MuiChip-icon': {
                        color: theme.palette.primary.main,
                      }
                    }}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  {etut.konu ? (
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {etut.konu}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                  )}
                </StyledTableCell>
                <StyledTableCell>
                  <StatusChip
                    status={etut.sonuc}
                    label={etut.sonuc === 2 ? 'Derse Gelmedi' : 'Ders Yapıldı'}
                    size="small"
                    icon={etut.sonuc === 2 ? <CancelIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                    theme={undefined}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        size="small"
                        label="Öğrenci"
                        color={etut.isStudentApproved ? "success" : "default"}
                        variant={etut.isStudentApproved ? "filled" : "outlined"}
                      />
                      <Chip
                        size="small"
                        label="Öğretmen"
                        color={etut.isTeacherApproved ? "success" : "default"}
                        variant={etut.isTeacherApproved ? "filled" : "outlined"}
                      />
                    </Box>

                    {etut.isStudentApproved ? (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<ThumbDownAltIcon />}
                        onClick={() => handleWithdrawApproval(etut.recNo)}
                        disabled={approveMutation.isPending || withdrawMutation.isPending}
                        sx={{ mt: 1 }}
                      >
                        Onayı Geri Çek
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<ThumbUpAltIcon />}
                        onClick={() => handleApproveLesson(etut.recNo)}
                        disabled={approveMutation.isPending || withdrawMutation.isPending}
                        sx={{ mt: 1 }}
                      >
                        Onayla
                      </Button>
                    )}
                  </Box>
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Calculate statistics
  const calculateStats = () => {
    if (!data) return { total: 0, completed: 0, missed: 0, approved: 0 };
    const completed = data.filter(etut => etut.sonuc !== 2).length;
    const missed = data.filter(etut => etut.sonuc === 2).length;
    const approved = data.filter(etut => etut.isStudentApproved).length;
    return {
      total: data.length,
      completed,
      missed,
      approved
    };
  };

  const stats = calculateStats();

  return (
    <Box sx={{ px: { xs: 1, sm: 2 } }}>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        mb: 3
      }}>
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 600,
            mb: { xs: 2, sm: 0 },
            color: theme.palette.text.primary
          }}
        >
          <MenuBookIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Birebir Etütler
        </Typography>
        <SeasonSelector
          variant="outlined"
          size="small"
          sx={{ minWidth: 200 }}
        >
          <InputLabel id="season-select-label">Eğitim Sezonu</InputLabel>
          <Select
            labelId="season-select-label"
            id="season-select"
            value={selectedSeason}
            onChange={handleSeasonChange}
            label="Eğitim Sezonu"
          >
            {availableSeasons.map((season) => (
              <MenuItem key={season} value={season}>
                {(parseInt(season) - 1)} - {season} Eğitim Yılı
              </MenuItem>
            ))}
          </Select>
        </SeasonSelector>
      </Box>

      {/* Statistics Cards - Only show when data is available */}
      {!isLoading && !error && data && data?.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={3} sx={{
              height: '100%',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: theme.shadows[6],
              },
            }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                  <MenuBookIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">{stats.total}</Typography>
                  <Typography variant="body2" color="text.secondary">Toplam Etüt</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={3} sx={{
              height: '100%',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: theme.shadows[6],
              },
            }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                  <CheckCircleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">{stats.completed}</Typography>
                  <Typography variant="body2" color="text.secondary">Tamamlanan Etüt</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={3} sx={{
              height: '100%',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: theme.shadows[6],
              },
            }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.error.main }}>
                  <CancelIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">{stats.missed}</Typography>
                  <Typography variant="body2" color="text.secondary">Kaçırılan Etüt</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={3} sx={{
              height: '100%',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: theme.shadows[6],
              },
            }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.info.main }}>
                  <ThumbUpAltIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">{stats.approved}</Typography>
                  <Typography variant="body2" color="text.secondary">Onaylanan Etüt</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filter chips - Only show when data is available */}
      {!isLoading && !error && data && data.length > 0 && (
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            <FilterListIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
            Filtrele:
          </Typography>
          <Chip
            label="Tümü"
            color={statusFilter === null ? "primary" : "default"}
            variant={statusFilter === null ? "filled" : "outlined"}
            onClick={() => setStatusFilter(null)}
            sx={{ fontWeight: statusFilter === null ? 'bold' : 'normal' }}
          />
          <Chip
            label="Ders Yapıldı"
            color={statusFilter === 1 ? "success" : "default"}
            variant={statusFilter === 1 ? "filled" : "outlined"}
            onClick={() => setStatusFilter(1)}
            icon={<CheckCircleIcon />}
            sx={{ fontWeight: statusFilter === 1 ? 'bold' : 'normal' }}
          />
          <Chip
            label="Derse Gelmedi"
            color={statusFilter === 2 ? "error" : "default"}
            variant={statusFilter === 2 ? "filled" : "outlined"}
            onClick={() => setStatusFilter(2)}
            icon={<CancelIcon />}
            sx={{ fontWeight: statusFilter === 2 ? 'bold' : 'normal' }}
          />
        </Box>
      )}

      {isLoading ? (
        <Box sx={{ mt: 3 }}>
          {renderSkeletons()}
        </Box>
      ) : error ? (
        <Alert
          severity="error"
          variant="filled"
          sx={{
            mt: 2,
            borderRadius: theme.shape.borderRadius * 1.5,
            boxShadow: theme.shadows[3]
          }}
          action={
            process.env.NODE_ENV === 'development' && (
              <Typography variant="caption" display="block" sx={{ color: 'white' }}>
                CorrelationId: {(error as BirebirError).correlationId}
              </Typography>
            )
          }
        >
          {(error as Error).message}
        </Alert>
      ) : !data?.length ? (
        <Alert
          severity="info"
          variant="outlined"
          icon={<MenuBookIcon />}
          sx={{
            mt: 2,
            borderRadius: theme.shape.borderRadius * 1.5,
            boxShadow: theme.shadows[1],
            bgcolor: theme.palette.background.paper
          }}
        >
          <Typography variant="body1">
            {parseInt(selectedSeason) - 1} - {selectedSeason} eğitim sezonunda kayıtlı birebir etüt bulunmamaktadır.
          </Typography>
        </Alert>
      ) : filteredData?.length === 0 ? (
        <Alert
          severity="info"
          variant="outlined"
          icon={<FilterListIcon />}
          sx={{
            mt: 2,
            borderRadius: theme.shape.borderRadius * 1.5,
            boxShadow: theme.shadows[1],
            bgcolor: theme.palette.background.paper
          }}
        >
          <Typography variant="body1">
            Seçilen filtreye uygun birebir etüt bulunmamaktadır.
          </Typography>
        </Alert>
      ) : (
        <Box sx={{ mt: { xs: 2, sm: 3 } }}>
          {isTablet ? renderCardView() : renderTableView()}
        </Box>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert2
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert2>
      </Snackbar>
    </Box>
  );
};

export default Birebir;